import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade } from "swiper/modules";

import "swiper/css";
import "swiper/css/effect-fade";

import {
  IoChevronBack,
  IoChevronForward,
  IoImageOutline,
} from "react-icons/io5";

import { useData } from "../data/DataProvider";

// Mobil uygulamadaki mantığın aynısı: process.env.MAIN_API_URL üzerinden istek atar
const getImageSource = (img) => {
  if (!img) return null;

  if (typeof img === "string") {
    // Tam URL zaten verilmişse doğrudan dön
    if (img.startsWith("http://") || img.startsWith("https://")) {
      return img;
    }

    const baseUrl = process.env.MAIN_API_URL || "";

    // Slash (/) çakışmalarını önlemek için güvenli birleştirme
    if (baseUrl.endsWith("/") && img.startsWith("/")) {
      return `${baseUrl}${img.slice(1)}`;
    }
    return `${baseUrl}${img}`;
  }

  return img.uri || img;
};

export default function LoginSlider() {
  const { announcementsData, getAnnouncementsData, loading } = useData();

  const [swiperInstance, setSwiperInstance] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    getAnnouncementsData();
  }, []);

  const announcements = Array.isArray(announcementsData)
    ? announcementsData
    : Array.isArray(announcementsData?.data)
      ? announcementsData.data
      : [];

  const handlePrev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (swiperInstance) {
      swiperInstance.slidePrev();
    }
  };

  const handleNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (swiperInstance) {
      swiperInstance.slideNext();
    }
  };

  const handleDotClick = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    if (swiperInstance) {
      swiperInstance.slideToLoop(index);
    }
  };

  if (loading && announcements.length === 0) {
    return (
      <div className="hidden lg:flex flex-1 rounded-3xl shadow-xl overflow-hidden relative min-h-[580px] bg-gray-900 animate-pulse items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-white/50">
          <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-medium">Duyurular Yükleniyor...</span>
        </div>
      </div>
    );
  }

  if (announcements.length === 0) {
    return (
      <div className="hidden lg:flex flex-1 rounded-3xl shadow-xl overflow-hidden relative min-h-[580px] bg-gray-900 items-center justify-center text-gray-400">
        <div className="flex flex-col items-center gap-2">
          <IoImageOutline className="text-5xl" />
          <span className="text-sm">Aktif duyuru bulunmuyor</span>
        </div>
      </div>
    );
  }

  return (
    <div className="hidden lg:flex flex-1 rounded-3xl shadow-xl overflow-hidden relative min-h-[580px] bg-gray-900">
      <Swiper
        key={announcements.length}
        onSwiper={setSwiperInstance}
        onRealIndexChange={(swiper) => setCurrentSlide(swiper.realIndex)}
        modules={[Autoplay, EffectFade]}
        effect="fade"
        speed={700}
        loop={announcements.length > 1}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        className="w-full h-full text-left"
      >
        {announcements.map((slide) => {
          const imageSrc = getImageSource(slide.image);

          return (
            <SwiperSlide key={slide.id} className="relative w-full h-full">
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={slide.title}
                  className="w-full h-full object-cover select-none"
                  onError={(e) => {
                    console.log("Resim yükleme hatası:", e);
                  }}
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-500 gap-3 bg-gray-800">
                  <IoImageOutline className="text-5xl" />
                  <span className="text-sm">Görsel bulunmuyor</span>
                </div>
              )}

              <div className="absolute inset-0 bg-black/20 z-10" />
            </SwiperSlide>
          );
        })}
      </Swiper>

      <div className="absolute inset-0 z-30 flex items-end justify-start pointer-events-none">
        <div className="bg-[#0a192f]/80 backdrop-blur-md border-t border-r border-white/10 rounded-tr-2xl p-6 text-white shadow-2xl flex flex-col w-full max-w-sm h-[200px] justify-between pointer-events-auto">
          <div>
            <span className="inline-block bg-blue-500/20 text-blue-300 text-[10px] font-bold tracking-wider uppercase px-2.5 py-0.5 rounded-full border border-blue-400/20">
              Duyuru
            </span>
            <h3 className="text-base font-bold mt-3 mb-1.5 leading-tight text-white line-clamp-2">
              {announcements[currentSlide]?.title}
            </h3>
            <p className="text-gray-300 text-xs leading-relaxed line-clamp-3">
              {announcements[currentSlide]?.description}
            </p>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-white/10">
            <div className="flex gap-1.5">
              {announcements.map((_, dotIndex) => (
                <button
                  key={dotIndex}
                  type="button"
                  onClick={(e) => handleDotClick(e, dotIndex)}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    dotIndex === currentSlide
                      ? "w-5 bg-blue-400"
                      : "w-1 bg-white/30"
                  }`}
                />
              ))}
            </div>

            {announcements.length > 1 && (
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={handlePrev}
                  className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white border border-white/10 transition-colors cursor-pointer active:scale-95"
                >
                  <IoChevronBack className="text-xs" />
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white border border-white/10 transition-colors cursor-pointer active:scale-95"
                >
                  <IoChevronForward className="text-xs" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
